import type { EntityConfig, LookupState, RowRecord } from '../types';
import { advancementTypeOptions, appUserRoleOptions, assignmentRoleOptions, backToBackMatchDayToleranceOptions, lineTypeOptions, playerRatingBasisOptions, playerTierOptions, registrationStatusOptions, rosterStatusOptions, sexOptions, singlesPreferenceOptions, teamColorOptions, teamGenderTypeOptions, usStateOptions } from './options';
import { formatEdr } from '../utils/edr';
import { rosterRoleOptions } from '../utils/rosterRoles';

const yesNo = (value: unknown) => (value ? 'Yes' : 'No');
const active = (value: unknown) => (value ? 'Active' : 'Inactive');

const name = (row: RowRecord) => [row.first_name, row.last_name].filter(Boolean).join(' ');
const teamGenderType = (value: unknown) => teamGenderTypeOptions.find((option) => option.value === value)?.label || '';
const teamColor = (value: unknown) => teamColorOptions.find((option) => option.value === value)?.label || 'Gray';
const oneDecimal = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(1) : String(value);
};

export const entities: EntityConfig[] = [
  {
    key: 'organizations',
    title: 'Organizations',
    table: 'organizations',
    description: 'Manage tennis organizations that publish leagues, such as USTA, TCD, and UTR.',
    orderBy: 'name',
    sortableColumns: ['name', 'website_url', 'active_status'],
    defaultSort: [{ field: 'name', direction: 'asc' }],
    columns: [
      { name: 'name', label: 'Organization' },
      { name: 'website_url', label: 'Website' },
      { name: 'active_status', label: 'Status', format: active },
    ],
    fields: [
      { name: 'name', label: 'Organization Name', type: 'text', required: true },
      { name: 'website_url', label: 'Website URL', type: 'url' },
      { name: 'active_status', label: 'Active', type: 'boolean' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'league-configurations',
    title: 'League Configurations',
    table: 'league_configurations',
    description: 'Manage league format definitions. Positions are configured separately.',
    orderBy: 'configuration_name',
    columns: [
      { name: 'configuration_name', label: 'Configuration' },
      { name: 'match_format_name', label: 'Format' },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'workspace_id', label: 'Workspace', type: 'select', optionsKey: 'workspaces', required: true },
      { name: 'configuration_name', label: 'Configuration Name', type: 'text', required: true },
      { name: 'match_format_name', label: 'Match Format Name', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
    ],
  },
  {
    key: 'league-positions',
    title: 'League Positions',
    table: 'league_configuration_positions',
    description: 'Define lineup positions for each league configuration.',
    orderBy: 'display_order',
    columns: [
      { name: 'league_configuration_id', label: 'Configuration', lookupKey: 'league_configurations' },
      { name: 'position_code', label: 'Code' },
      { name: 'display_name', label: 'Display Name' },
      { name: 'display_order', label: 'Order' },
      { name: 'line_type', label: 'Line Type' },
      { name: 'required_player_count', label: 'Players' },
      { name: 'required_male_count', label: 'Male' },
      { name: 'required_female_count', label: 'Female' },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'league_configuration_id', label: 'League Configuration', type: 'select', optionsKey: 'league_configurations', required: true },
      { name: 'position_code', label: 'Position Code', type: 'text', required: true },
      { name: 'display_name', label: 'Display Name', type: 'text', required: true },
      { name: 'display_order', label: 'Display Order', type: 'number', required: true },
      { name: 'line_type', label: 'Line Type', type: 'select', options: lineTypeOptions, required: true },
      { name: 'required_player_count', label: 'Required Player Count', type: 'number', required: true },
      { name: 'required_male_count', label: 'Required Male Count', type: 'number' },
      { name: 'required_female_count', label: 'Required Female Count', type: 'number' },
      { name: 'is_doubles', label: 'Doubles', type: 'boolean' },
      { name: 'counts_toward_fairness', label: 'Counts Toward Fairness', type: 'boolean' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
    ],
  },
  {
    key: 'users',
    title: 'Users',
    table: 'app_users',
    description: 'Manage people who can administer the application or be assigned as team captains.',
    orderBy: 'display_name',
    sortableColumns: ['display_name', 'email', 'role', 'linked_player_id', 'is_active'],
    defaultSort: [{ field: 'display_name', direction: 'asc' }],
    columns: [
      { name: 'display_name', label: 'Display Name' },
      { name: 'email', label: 'Email' },
      { name: 'role', label: 'Role' },
      { name: 'linked_player_id', label: 'Linked Player', lookupKey: 'players' },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'display_name', label: 'Display Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'role', label: 'Role', type: 'select', options: appUserRoleOptions, required: true },
      { name: 'linked_player_id', label: 'Linked Player', type: 'select', optionsKey: 'players' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
    ],
  },
  {
    key: 'players',
    title: 'Players',
    table: 'players',
    description: 'Manage reusable player profiles across teams and seasons.',
    orderBy: 'last_name',
    sortableColumns: ['full_name', 'sex', 'singles_preference', 'ntrp_rating', 'estimated_dynamic_rating', 'tennis_record_sync_status', 'utr_singles_rating', 'utr_doubles_rating', 'email', 'mobile_phone', 'is_active'],
    defaultSort: [{ field: 'full_name', direction: 'asc' }],
    columns: [
      { name: 'full_name', label: 'Name', format: (_value, row) => name(row) },
      { name: 'email', label: 'Email' },
      { name: 'mobile_phone', label: 'Phone' },
      { name: 'groupme_display_name', label: 'GroupMe Name' },
      { name: 'sex', label: 'Gender' },
      { name: 'singles_preference', label: 'Singles Preference' },
      { name: 'utr_singles_rating', label: 'UTR Singles' },
      { name: 'utr_doubles_rating', label: 'UTR Doubles' },
      { name: 'ntrp_rating', label: 'NTRP', format: oneDecimal },
      { name: 'estimated_dynamic_rating', label: 'EDR', format: formatEdr },
      { name: 'tennis_record_sync_status', label: 'TennisRecord Sync' },
      { name: 'player_profiles', label: 'Player Profiles' },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'first_name', label: 'First Name', type: 'text', required: true },
      { name: 'last_name', label: 'Last Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'mobile_phone', label: 'Mobile Phone', type: 'text' },
      { name: 'groupme_display_name', label: 'GroupMe Display Name', type: 'text' },
      { name: 'birthday', label: 'Birthday', type: 'date' },
      { name: 'address_line1', label: 'Address Line 1', type: 'text' },
      { name: 'address_line2', label: 'Address Line 2', type: 'text' },
      { name: 'city', label: 'City', type: 'text' },
      { name: 'state', label: 'State', type: 'text' },
      { name: 'zip_code', label: 'Zip Code', type: 'text' },
      { name: 'utr_rating', label: 'UTR Rating', type: 'number' },
      { name: 'utr_singles_rating', label: 'UTR Singles Rating', type: 'number' },
      { name: 'utr_doubles_rating', label: 'UTR Doubles Rating', type: 'number' },
      { name: 'ntrp_rating', label: 'NTRP Rating', type: 'number' },
      { name: 'estimated_dynamic_rating', label: 'Estimated Dynamic Rating (EDR)', type: 'number' },
      { name: 'utr_profile_url', label: 'UTR Profile URL', type: 'url' },
      { name: 'tennis_record_profile_url', label: 'TennisRecord Profile URL', type: 'url' },
      { name: 'usta_profile_url', label: 'USTA Profile URL', type: 'url' },
      { name: 'sex', label: 'Gender', type: 'select', options: sexOptions, required: true },
      { name: 'singles_preference', label: 'Singles Preference', type: 'select', options: singlesPreferenceOptions },
      { name: 'back_to_back_match_day_tolerance', label: 'Back-to-Back Match Day Tolerance', type: 'select', options: backToBackMatchDayToleranceOptions, helperText: 'How strongly CourtCaptain should avoid consecutive match-day assignments for this player.' },
      { name: 'same_day_match_tolerance', label: 'Same-Day Match Tolerance', type: 'select', options: backToBackMatchDayToleranceOptions, helperText: 'How strongly CourtCaptain should avoid assigning this player to multiple matches on the same day.' },
      { name: 'minimum_recovery_hours', label: 'Minimum Recovery Hours', type: 'number', helperText: 'Preferred minimum time between match assignments.' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'facilities',
    title: 'Facilities',
    table: 'facilities',
    description: 'Manage reusable home and match facilities.',
    orderBy: 'facility_name',
    sortableColumns: ['facility_name', 'state', 'city', 'zip_code', 'is_active'],
    defaultSort: [
      { field: 'state', direction: 'asc' },
      { field: 'city', direction: 'asc' },
      { field: 'facility_name', direction: 'asc' },
    ],
    columns: [
      { name: 'facility_name', label: 'Facility' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
      { name: 'zip_code', label: 'Zip' },
      { name: 'is_active', label: 'Active Status', format: active },
    ],
    fields: [
      { name: 'facility_name', label: 'Facility Name', type: 'text', required: true },
      { name: 'address_line1', label: 'Address Line 1', type: 'text' },
      { name: 'address_line2', label: 'Address Line 2', type: 'text' },
      { name: 'city', label: 'City', type: 'text' },
      { name: 'state', label: 'State', type: 'select', options: usStateOptions, required: true },
      { name: 'zip_code', label: 'Zip Code', type: 'text' },
      { name: 'google_maps_url', label: 'Google Maps URL', type: 'text' },
      { name: 'warmup_booking_url', label: 'Facility Website', type: 'url' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'teams',
    title: 'Teams',
    table: 'teams',
    description: 'Create season-specific teams and assign organization, league details, and home facility.',
    orderBy: 'team_name',
    sortableColumns: ['team_name', 'workspace_id', 'organization_id', 'league_name', 'team_flight', 'season_label', 'league_configuration_id', 'advancement_type', 'player_rating_basis', 'team_gender_type', 'team_color', 'home_facility_id', 'default_target_match_count_per_player', 'default_warmup_minutes', 'player_portal_access_enabled', 'player_event_self_service_enabled', 'player_portal_tentative_lineups_enabled', 'player_portal_tentative_lineup_lookahead_matches', 'is_active'],
    defaultSort: [{ field: 'team_name', direction: 'asc' }],
    columns: [
      { name: 'team_name', label: 'Team' },
      { name: 'short_name', label: 'Short Name' },
      { name: 'workspace_id', label: 'Workspace', lookupKey: 'workspaces' },
      { name: 'organization_id', label: 'Organization', lookupKey: 'organizations' },
      { name: 'league_name', label: 'League Name' },
      { name: 'team_flight', label: 'TCD Flight' },
      { name: 'season_label', label: 'Season Label' },
      { name: 'league_configuration_id', label: 'Match Format', lookupKey: 'league_configurations' },
      { name: 'advancement_type', label: 'Advancement' },
      { name: 'player_rating_basis', label: 'Player Ranking' },
      { name: 'team_gender_type', label: 'Team Type', format: teamGenderType },
      { name: 'team_color', label: 'Team Color', format: teamColor },
      { name: 'home_facility_id', label: 'Home Facility', lookupKey: 'facilities' },
      { name: 'default_target_match_count_per_player', label: 'Default Target' },
      { name: 'default_warmup_minutes', label: 'Default Warmup' },
      { name: 'player_portal_access_enabled', label: 'Player Portal', format: yesNo },
      { name: 'player_event_self_service_enabled', label: 'Player Event Self-Service', format: yesNo },
      { name: 'player_portal_tentative_lineups_enabled', label: 'Tentative Lineups', format: yesNo },
      { name: 'player_portal_tentative_lineup_lookahead_matches', label: 'Tentative Lookahead (Matches)' },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'team_name', label: 'Team Name', type: 'text', required: true },
      { name: 'short_name', label: 'Short Name', type: 'text', maxLength: 40, helperText: 'Optional player-facing name for compact spaces such as calendars and legends. Aim for 20 characters or fewer.' },
      { name: 'workspace_id', label: 'Workspace', type: 'select', optionsKey: 'workspaces', required: true },
      { name: 'organization_id', label: 'Organization', type: 'select', optionsKey: 'organizations', required: true },
      { name: 'season_label', label: 'Season Label', type: 'text', helperText: 'Optional description of this team instance, such as Fall 2026. Match dates come from the schedule.' },
      { name: 'league_configuration_id', label: 'Match Format', type: 'select', optionsKey: 'league_configurations', required: true },
      { name: 'advancement_type', label: 'Advancement Type', type: 'select', options: advancementTypeOptions, required: true },
      { name: 'player_rating_basis', label: 'Player Ranking Basis', type: 'select', options: playerRatingBasisOptions, required: true },
      { name: 'team_gender_type', label: 'Team Type', type: 'select', options: teamGenderTypeOptions, required: true },
      { name: 'team_color', label: 'Team Color', type: 'select', options: teamColorOptions, required: true },
      { name: 'player_portal_access_enabled', label: 'Enable Player Portal for This Team', type: 'boolean' },
      { name: 'player_event_self_service_enabled', label: 'Enable Player Event Self-Service for This Team', type: 'boolean', helperText: 'Effective only when enabled globally in App Settings.' },
      { name: 'player_portal_tentative_lineups_enabled', label: 'Show Tentative Lineups to Players', type: 'boolean', helperText: 'Off by default. Allows rostered players to view planned lineups for the next matches.' },
      { name: 'player_portal_tentative_lineup_lookahead_matches', label: 'Tentative Lineup Lookahead (Matches)', type: 'number', required: true, helperText: 'Count all upcoming matches, including those with published or no lineup. 1–20 matches.' },
      { name: 'league_name', label: 'League Name', type: 'text' },
      { name: 'team_flight', label: 'TCD Flight', type: 'text', helperText: 'Printed in the Flight field on TCD scoresheets.', maxLength: 30 },
      { name: 'usta_team_number', label: 'USTA Team Number', type: 'text' },
      { name: 'team_page_url', label: 'Team Page URL', type: 'text' },
      { name: 'groupme_group_id', label: 'GroupMe Group ID', type: 'text' },
      { name: 'groupme_group_name', label: 'GroupMe Group Name', type: 'text' },
      { name: 'groupme_event_reminders_enabled', label: 'Enable GroupMe Conversation Reminders', type: 'boolean', helperText: 'Applies the two-reminder defaults to newly created events. This is separate from GroupMe Event Sync.' },
      { name: 'default_groupme_event_reminders', label: 'Default GroupMe Event Reminders', type: 'text' },
      { name: 'home_match_opponent_message_template', label: 'Home Match Opponent Message Template', type: 'textarea' },
      { name: 'home_facility_id', label: 'Home Facility', type: 'select', optionsKey: 'facilities' },
      { name: 'default_target_match_count_per_player', label: 'Default Target Matches Per Player', type: 'number' },
      { name: 'default_warmup_minutes', label: 'Default Warmup Minutes', type: 'number', required: true },
      { name: 'enforce_target_matches', label: 'Enforce Target Matches', type: 'boolean' },
      { name: 'target_match_variance', label: 'Target Match Variance', type: 'number', helperText: 'Use 0 for the exact target, or a higher whole number for allowed variance.' },
      { name: 'enforce_max_sit', label: 'Avoid Sitting More Than X Weeks', type: 'boolean' },
      { name: 'max_weeks_without_match', label: 'Maximum Weeks Without a Match', type: 'number', helperText: 'Uses actual match dates to help prioritize players who are approaching or exceeding this many weeks without playing.' },
      { name: 'use_partner_compatibility', label: 'Use Partner Compatibility', type: 'boolean' },
      { name: 'use_partner_scheduling_preferences', label: 'Use Partner Scheduling Preferences', type: 'boolean' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'captain-assignments',
    title: 'Captain Assignments',
    table: 'team_captain_assignments',
    description: 'Assign existing users as captains or co-captains for teams.',
    orderBy: 'created_at',
    sortableColumns: ['team_id', 'app_user_id', 'assignment_role', 'is_primary_captain', 'is_active'],
    defaultSort: [
      { field: 'team_id', direction: 'asc' },
      { field: 'is_primary_captain', direction: 'desc' },
    ],
    columns: [
      { name: 'team_id', label: 'Team', lookupKey: 'teams' },
      { name: 'app_user_id', label: 'Captain', lookupKey: 'app_users' },
      { name: 'assignment_role', label: 'Role' },
      { name: 'is_primary_captain', label: 'Primary', format: yesNo },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'team_id', label: 'Team', type: 'select', optionsKey: 'teams', required: true },
      { name: 'app_user_id', label: 'Captain User', type: 'select', optionsKey: 'app_users', required: true },
      { name: 'assignment_role', label: 'Assignment Role', type: 'select', options: assignmentRoleOptions, required: true },
      { name: 'is_primary_captain', label: 'Primary Captain', type: 'boolean' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    key: 'rosters',
    title: 'Team Rosters',
    table: 'team_rosters',
    description: 'Assign players to teams with target matches, tier, and roster status.',
    orderBy: 'created_at',
    columns: [
      { name: 'team_id', label: 'Team', lookupKey: 'teams' },
      { name: 'player_id', label: 'Player', lookupKey: 'players' },
      { name: 'target_match_count', label: 'Target' },
      { name: 'player_tier', label: 'Tier' },
      { name: 'roster_role', label: 'Roster Role' },
      { name: 'roster_status', label: 'Roster Status' },
      { name: 'registration_status', label: 'Registration Status' },
      { name: 'is_active', label: 'Status', format: active },
    ],
    fields: [
      { name: 'team_id', label: 'Team', type: 'select', optionsKey: 'teams', required: true },
      { name: 'player_id', label: 'Player', type: 'select', optionsKey: 'players', required: true },
      { name: 'target_match_count', label: 'Target Match Count', type: 'number' },
      { name: 'player_tier', label: 'Player Tier', type: 'select', options: playerTierOptions },
      { name: 'roster_role', label: 'Roster Role', type: 'select', options: rosterRoleOptions, required: true },
      { name: 'roster_status', label: 'Roster Status', type: 'select', options: rosterStatusOptions, required: true },
      { name: 'registration_status', label: 'Registration Status', type: 'select', options: registrationStatusOptions, required: true },
      { name: 'registration_verified_at', label: 'Registration Verified Date', type: 'date' },
      { name: 'registration_verified_by', label: 'Registration Verified By', type: 'select', optionsKey: 'app_users' },
      { name: 'registration_notes', label: 'Registration Notes', type: 'textarea' },
      { name: 'is_active', label: 'Active', type: 'boolean' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
];

export function getEntity(key: string): EntityConfig {
  const entity = entities.find((item) => item.key === key);
  if (!entity) return entities[0];
  return entity;
}

export function labelForValue(lookups: LookupState, row: RowRecord, field: string): string {
  const entity = entities.find((item) => item.fields.some((candidate) => candidate.name === field));
  const column = entity?.columns.find((candidate) => candidate.name === field);
  if (!column?.lookupKey || typeof row[field] !== 'string') return row[field] == null ? '' : String(row[field]);
  return lookups[column.lookupKey].find((item) => item.value === row[field])?.label || String(row[field]);
}
